import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { Box, InputAdornment, Tooltip } from "@mui/material";
import { mesfColors } from "@/setup/ag-grid/mesf-theme";
import { NumericStepField } from "../../straightener-common/components/NumericStepField";
import { resolveLimit } from "../../straightener-common/paramLimits";
import type { LimitOverrides } from "../../straightener-common/paramLimits";
import type { StraightenerParameter } from "../../straightener-common/parameterCatalog";

export type RollBand = "value" | "min" | "max";

const TARGET_ONLY: { band: RollBand; label: string }[] = [
	{ band: "value", label: "Target" },
];
const WITH_BAND: { band: RollBand; label: string }[] = [
	{ band: "value", label: "Target" },
	{ band: "min", label: "Min" },
	{ band: "max", label: "Max" },
];

interface Props {
	params: StraightenerParameter[];
	rollCount: number;
	getCell: (code: string, roll: number, band: RollBand) => string;
	setCell: (code: string, roll: number, band: RollBand, value: string) => void;
	// Returns the error message for a cell (Target / Min / Max): out of the
	// parameter's limits, or an invalid band pair. Null = valid.
	cellError?: (code: string, roll: number, band: RollBand) => string | null;
	limitOverrides?: LimitOverrides;
	readOnly?: boolean;
}

// A roll value is a handful of digits ("0.891", "-0.04", "19.8"), so the column
// is sized for that and not for the header. Below this the table scrolls inside
// its own container rather than squeezing the numbers.
const CELL_WIDTH = 56;
const PARAM_COL_WIDTH = 92;

const headSx = {
	fontSize: "0.68rem",
	fontWeight: 700,
	color: "text.secondary",
	textAlign: "center" as const,
	py: 0.4,
	px: 0.25,
	borderBottom: "1px solid",
	borderColor: "divider",
	backgroundColor: "background.paper",
};

// The two label columns stay put while the rolls scroll sideways.
const stickySx = {
	position: "sticky" as const,
	backgroundColor: "background.paper",
	zIndex: 1,
};

const paramCellSx = {
	...stickySx,
	left: 0,
	width: PARAM_COL_WIDTH,
	fontSize: "0.72rem",
	fontWeight: 600,
	lineHeight: 1.2,
	color: "text.primary",
	verticalAlign: "middle" as const,
	py: 0.2,
	pl: 0.5,
	pr: 1,
	borderTop: `1px solid ${mesfColors.grey[200]}`,
};

const bandCellSx = {
	...stickySx,
	left: PARAM_COL_WIDTH,
	fontSize: "0.66rem",
	color: "text.secondary",
	whiteSpace: "nowrap" as const,
	textAlign: "right" as const,
	py: 0.2,
	pr: 0.75,
};

// Every roll parameter and every roll at once: the operator reads and edits the
// whole straightening setup in one place instead of stepping through one metric
// at a time. Target is the PLC setpoint; Min / Max are MES tolerance bands (not
// sent), and only the parameters that carry a band show those two rows.
export const StraightenerRollsTable = ({
	params,
	rollCount,
	getCell,
	setCell,
	cellError,
	limitOverrides,
	readOnly = false,
}: Props) => {
	const rolls = Array.from({ length: rollCount }, (_, i) => i + 1);
	if (!params.length) return null;

	return (
		<Box sx={{ overflowX: "auto" }}>
			{/* Sized to its content, not stretched: the card is laid out around the
			    table (the editor's grid gives this column `auto`), so the width the
			    numbers need is the width the column gets. */}
			<Box
				component="table"
				sx={{ borderCollapse: "separate", borderSpacing: 0, width: "auto" }}
			>
				<Box component="thead">
					<Box component="tr">
						<Box
							component="th"
							sx={{
								...headSx,
								...stickySx,
								left: 0,
								textAlign: "left",
								pl: 0.5,
							}}
						>
							Parameter
						</Box>
						<Box
							component="th"
							sx={{ ...headSx, ...stickySx, left: PARAM_COL_WIDTH }}
						/>
						{rolls.map((roll) => (
							<Box key={roll} component="th" sx={headSx}>
								Roll #{roll}
							</Box>
						))}
					</Box>
				</Box>
				<Box component="tbody">
					{params.map((p) => {
						const bands = p.hasMinMax ? WITH_BAND : TARGET_ONLY;
						const limit = resolveLimit(p.code, limitOverrides);
						return bands.map(({ band, label }, bandIndex) => (
							<Box component="tr" key={`${p.code}:${band}`}>
								{bandIndex === 0 ? (
									<Box component="td" rowSpan={bands.length} sx={paramCellSx}>
										{p.displayName}
									</Box>
								) : null}
								<Box
									component="td"
									sx={{
										...bandCellSx,
										borderTop:
											bandIndex === 0
												? `1px solid ${mesfColors.grey[200]}`
												: undefined,
									}}
								>
									{label}
								</Box>
								{rolls.map((roll) => {
									const message = cellError?.(p.code, roll, band) ?? null;
									return (
										<Box
											component="td"
											key={roll}
											sx={{
												py: 0.2,
												px: 0.25,
												borderTop:
													bandIndex === 0
														? `1px solid ${mesfColors.grey[200]}`
														: undefined,
											}}
										>
											<NumericStepField
												value={getCell(p.code, roll, band)}
												onValueChange={(v) => setCell(p.code, roll, band, v)}
												disabled={readOnly}
												error={!!message}
												min={limit.min}
												max={limit.max}
												density="cell"
												sx={{ width: CELL_WIDTH }}
												endAdornment={
													message ? (
														<InputAdornment
															position="end"
															sx={{ ml: 0, mr: -0.5 }}
														>
															<Tooltip title={message}>
																<WarningAmberIcon
																	sx={{
																		fontSize: "0.85rem",
																		color: "error.main",
																	}}
																/>
															</Tooltip>
														</InputAdornment>
													) : undefined
												}
											/>
										</Box>
									);
								})}
							</Box>
						));
					})}
				</Box>
			</Box>
		</Box>
	);
};
